<template>
  <div class="my-creation-page">
    <!-- 顶部导航栏 -->
    <div class="top-navbar">
      <div class="nav-item" @click="switchNavTab('home')">
        <i class="home-icon"></i>
        {{ langData.home || "首页" }}
      </div>
      <div class="nav-item" @click="switchNavTab('ugc')">
        <i class="fabu-icon"></i>
        {{ langData.publish || "我要发布" }}
      </div>
      <div class="center-btn"></div>
    </div>

    <!-- 按钮列表 -->
    <BtnList
      @showRulePopup="showRulePopup"
      @jumpToWebsite="jumpToWebsite"
      @jumpToFacebook="jumpToFacebook"
      @jumpToDiscord="jumpToDiscord"
    />

    <!-- 创作中心头部 -->
    <div class="center-header">
      <div class="center-info" @click="backUgcList()">
        <i class="back-icon"></i>
        <div class="back-text">{{ langData.backText }}</div>
      </div>
      <i
        class="notice-icon"
        style="display: inline-block"
        :class="{ active: isNotice }"
        @click="showAuditNotices()"
      ></i>
    </div>

    <!-- 我的作品容器 -->
    <div class="my-works-container">
      <div class="my-works-type" :class="[`type-${activeWorksType}`]"></div>
      <!-- 作品图片轮播 - 使用 Swiper -->
      <div class="works-image-slider">
        <template v-if="myWorksImages.length > 0">
          <swiper
            :modules="swiperModules"
            :slides-per-view="1"
            :space-between="0"
            :navigation="myWorksImages.length > 1"
            :pagination="myWorksImages.length > 1 ? { clickable: true } : false"
            :loop="false"
            class="works-swiper"
            @slideChange="onSlideChange"
          >
            <swiper-slide
              v-for="(item, index) in myWorksImages"
              :key="index"
              class="works-slide"
            >
              <img
                class="slider-img"
                :src="item"
                alt="作品图片"
                @click="viewImage(item, '创作中心')"
                @error="handleImageError"
              />
            </swiper-slide>
          </swiper>
        </template>
        <div v-else class="default-slider"></div>
      </div>

      <!-- 用户作品列表 -->
      <div class="works-list-container">
        <div class="works-list">
          <div class="works-list-title">
            <i class="title-icon"></i>
            <span>{{ langData.myCreative }}</span>
          </div>
          <template v-if="myCreationList.length > 0">
            <template v-for="(item, index) in myCreationList" :key="index">
              <div
                class="work-item"
                :class="{ active: myWorkId === item?.ID }"
                style="cursor: pointer;"
                @click="toggleWork(item?.ID)"
              >
                <div class="work-item-top">
                  <div class="work-item-top-left">
                    <div class="work-title">
                      {{ item?.TITLE }}
                    </div>
                    <div class="work-item-bottom">
                      <div class="work-time">
                        {{ langData.publishTime + item?.DISPLAY_CREATE_TIME }}
                      </div>
                    </div>
                  </div>
                  <div class="work-item-top-center">
                    <div class="work-state">
                      {{ getStatusText(item.STATUS) }}
                    </div>
                  </div>
                  <div class="work-item-top-right">
                    <div class="work-likes">
                      <i
                        class="like-icon"
                        :class="{ liked: +item?.IS_LIKE }"
                        @click.stop="toggleWorkLike(item?.ID)"
                      ></i>
                      <span class="like-count">{{
                        item?.DISPLAY_LIKE_COUNT
                      }}</span>
                    </div>
                    <div
                      class="work-delete-btn"
                      @click.stop="showDeleteConfirm(item)"
                    ></div>
                  </div>
                </div>
                <div class="work-text">
                  {{ item?.CONTENT }}
                </div>
              </div>
              <div
                class="work-item-split"
                v-if="index !== myCreationList.length - 1"
              ></div>
            </template>
          </template>
          <div v-else class="empty-works">
            <div class="empty-text">{{ langData.emptyWorks }}</div>
          </div>
        </div>
        <!-- 分页器 -->
        <div class="pagination" v-if="pageInfo.totalPage >= 1">
          <div
            class="pagination-prev"
            :class="{ disabled: pageInfo.currentPage === 1 }"
            @click="handlePageChange(pageInfo.currentPage - 1)"
          ></div>
          <div class="pagination-pages">
            <div
              v-for="(page, idx) in visiblePages"
              :key="idx"
              class="pagination-page"
              :class="{
                active: pageInfo.currentPage === page,
                ellipsis: page === '...',
              }"
              @click="page !== '...' ? handlePageChange(page) : null"
            >
              {{ page }}
            </div>
          </div>
          <div
            class="pagination-next"
            :class="{ disabled: pageInfo.currentPage === pageInfo.totalPage }"
            @click="handlePageChange(pageInfo.currentPage + 1)"
          ></div>
        </div>
      </div>
    </div>

    <!-- 加载更多提示 -->
    <div class="loading-more" v-if="isLoading && hasMoreData">
      {{ isLoading ? langData.loading : langData.loadingMore }}
    </div>
    <div class="no-more-data" v-if="!hasMoreData && myCreationList.length > 0">
      {{ langData.noMoreData }}
    </div>

    <!-- 删除确认弹窗 -->
    <DeleteConfirmPopup
      :visible="deleteConfirmState"
      :title="langData.deleteConfirmTitle || '确认删除'"
      :message="
        langData.deleteConfirmText || '确定要删除这个作品吗？删除后无法恢复。'
      "
      :confirmText="langData.confirm || '确定'"
      :cancelText="langData.cancel || '取消'"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />

    <!-- 审核通知弹窗 -->
    <AuditNoticesPopup
      :visible="auditNoticesPopupState"
      :notices="auditNotices"
      :title="langData.auditResultNotice || '审核结果通知'"
      :emptyText="langData.emptyNotice || '暂无审核通知'"
      :contentTemplate="
        langData.auditResultContent || '您于 ${time} 提交的作品《${title}》'
      "
      :reasonLabel="langData.auditReason || '原因：'"
      :timeLabel="langData.auditTime || '通知时间：'"
      @close="auditNoticesPopupState = false"
    />

    <!-- 页面底部导航 -->
    <!-- <div class="bottom-navbar sticky">
      <div class="nav-item" @click="switchNavTab('home')">
        <div class="nav-icon home-icon"></div>
        <div class="nav-text">{{ langData.home }}</div>
      </div>
      <div class="creation-btn" @click="showCreatePopup()"></div>
      <div class="nav-item" @click="switchNavTab('center')">
        <div class="nav-icon center-icon"></div>
        <div class="nav-text">{{ langData.creativeCenter }}</div>
      </div>
    </div> -->
  </div>
</template>

<script setup>
import { ref, inject, computed, onMounted, onUnmounted, watch, reactive } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination } from "swiper/modules";
import LanguageHook from "@/hooks/language";
import { getJSON, getActivityApi } from "@/utils";
import BtnList from "@/components/BtnList.vue";
import DeleteConfirmPopup from "@/components/DeleteConfirmPopup.vue";
import AuditNoticesPopup from "@/components/AuditNoticesPopup.vue";
import { useRulePopup } from "@/hooks/usePopup";

// 导入 Swiper 样式
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Props & Emits
const emit = defineEmits([
  "goBack",
  "showCreatePopup",
  "switchNavTab",
  "showContentDetail",
  "deleteCreation",
  "previewImage",
  "showLoginTip",
  "jumpToWebsite",
  "jumpToFacebook",
  "jumpToDiscord",
]);

// 注入的数据
const activeNavTab = inject("activeNavTab", "center");
const isLogin = inject("isLogin", ref(false));
const refreshCenterData = inject("refreshCenterData", ref(null));

// 规则弹窗 composable
const { showRulePopup: showRulePopupFn } = useRulePopup();

// 语言相关
const { langData } = LanguageHook();

// Swiper 模块配置
const swiperModules = [Navigation, Pagination];

// 响应式数据
const myCreationList = ref([]);
const myWorks = ref([]);
const myWorksImages = ref([]);
const myWorkId = ref("");
const currentImageIndex = ref(0);
const isNotice = ref(false);
const isLoading = ref(false);
const hasMoreData = ref(true);
const activeWorksType = ref("");

const pageInfo = reactive({
  currentPage: 1,
  pageSize: 10,
  totalPage: 0,
});

// 页面停留时间追踪
const viewPageStartTime = ref(null);

// 计算浏览时长
const formatDuration = (startTime, endTime) => {
  const duration = Math.floor((endTime - startTime) / 1000); // 转换为秒
  // 格式化为 xxHxxMxxS
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;
  return `${hours > 0 ? hours + 'H' : ''}${minutes > 0 ? minutes + 'M' : ''}${seconds}S`;
};

// 上报页面停留时间
const reportStayTime = () => {
  if (viewPageStartTime.value) {
    window.activityDataLayer.push({
      eventName: 'stay_time',
      eventType: '作品管理',
      eventValue: formatDuration(viewPageStartTime.value, new Date()),
      aid: window.activity_name,
      gameId: window.game_id
    });
  }
};

// 计算属性
const isEmpty = computed(() => myCreationList.value.length === 0);

// 获取当前选中的作品
const currentWork = computed(() => {
  if (!myWorkId.value) return null;
  return (
    myCreationList.value.find((item) => item.ID === myWorkId.value) || null
  );
});

// 计算可见的页码
const visiblePages = computed(() => {
  const total = pageInfo.totalPage;
  const current = pageInfo.currentPage;
  const pages = [];

  if (total <= 7) {
    // 总页数小于等于7，显示所有页码
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // 总页数大于7，显示部分页码
    if (current <= 4) {
      // 当前页靠前
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(total);
    } else if (current >= total - 3) {
      // 当前页靠后
      pages.push(1);
      pages.push("...");
      for (let i = total - 4; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // 当前页在中间
      pages.push(1);
      pages.push("...");
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(total);
    }
  }

  return pages;
});

// 方法
const goBack = () => emit("goBack");
const showCreatePopup = () => emit("showCreatePopup");
const switchNavTab = (tab) => {
  emit("switchNavTab", tab);
};
const showContentDetail = (item) => emit("showContentDetail", item, true); // 第二个参数表示从个人中心打开
const deleteCreation = (id) => emit("deleteCreation", id);

const getStatusText = (status) => {
  if (+status === 0) {
    return langData.auditState || "审核中";
  } else if (+status === 1) {
    return langData.auditPass || "已通过";
  } else {
    return langData.auditFail || "未通过";
  }
};

const loadMyCreations = async () => {
  if (!isLogin.value) {
    console.warn("用户未登录，跳过加载创作数据");
    return;
  }

  isLoading.value = true;
  try {
    await loadRealMyCreations();
  } catch (error) {
    console.error("加载我的创作失败:", error);
  } finally {
    isLoading.value = false;
  }
};

// 真实API加载我的创作数据
const loadRealMyCreations = async () => {
  const { data, result, code } = await getJSON(
    getActivityApi(window.platform_id, window.activity_name, "myUgcList"),
    {
      params: JSON.stringify({
        page: pageInfo.currentPage,
        pageSize: pageInfo.pageSize,
      }),
    }
  );

  if (+result === 1) {
    // pageTotal 是总记录数，需要计算总页数
    const totalCount = data?.pageTotal || 0;
    isNotice.value = !!data?.isNotice || false;
    pageInfo.totalPage = Math.ceil(totalCount / pageInfo.pageSize);
    const creations = data?.ugcContentList || [];

    // 处理数据显示格式
    myCreationList.value = creations.map((item) => ({
      ...item,
      STATUS: item.STATE || item.STATUS || 0, // 统一使用 STATUS 字段
      DISPLAY_LOGIN_ACCOUNT:
        item.LOGIN_ACCOUNT?.length > 3
          ? item.LOGIN_ACCOUNT.slice(0, 2) +
            "..." +
            item.LOGIN_ACCOUNT.slice(-1)
          : item.LOGIN_ACCOUNT,
      DISPLAY_LIKE_COUNT:
        +item.LIKE_COUNT >= 10000
          ? `${(+item.LIKE_COUNT / 10000).toFixed(1)}W`
          : +item.LIKE_COUNT >= 1000
          ? `${(+item.LIKE_COUNT / 1000).toFixed(1)}K`
          : item.LIKE_COUNT,
      DISPLAY_TITLE: item.TITLE,
      DISPLAY_CONTENT: item.CONTENT,
      DISPLAY_PIC_LIST: item.PIC_URL_LIST ? item.PIC_URL_LIST.split(",") : [],
      DISPLAY_PIC: item.PIC_URL_LIST
        ? item.PIC_URL_LIST.split(",")[0]
        : import.meta.env.BASE_URL + "default-pic.png",
      DISPLAY_AVATAR_ID: Math.floor(Math.random() * 3) + 1,
      DISPLAY_CREATE_TIME: item.CREATE_TIME
        ? formatDate(new Date(item.CREATE_TIME))
        : "",
    }));

    // 设置第一个作品的ID和图片为轮播图
    if (myCreationList.value.length > 0) {
      myWorkId.value = myCreationList.value[0].ID || "";
      if (
        myCreationList.value[0]?.DISPLAY_PIC_LIST &&
        myCreationList.value[0].DISPLAY_PIC_LIST.length > 0
      ) {
        myWorksImages.value = myCreationList.value[0].DISPLAY_PIC_LIST;
      }
      activeWorksType.value = myCreationList.value[0].UGC_TYPE;
    }
  } else {
    console.error(`API Error: [${code}] ${data?.msg || "Unknown error"}`);
  }
};

// 格式化日期
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

// Swiper 事件处理
const onSlideChange = (swiper) => {
  currentImageIndex.value = swiper.activeIndex;
};

// 图片加载错误处理
const handleImageError = (event) => {
  event.target.src = import.meta.env.BASE_URL + "/default-pic.png";
};

// 查看大图
const viewImage = (imageUrl, module) => {
  emit("previewImage", imageUrl);
};

// 作品切换功能
const toggleWork = (id) => {
  const itemIndex = myCreationList.value.findIndex((item) => item.ID === id);
  if (itemIndex === -1) {
    console.error("作品不存在，ID:", id);
    return;
  }

  myWorkId.value = id;
  myWorksImages.value = myCreationList.value[itemIndex].DISPLAY_PIC_LIST || [];
  activeWorksType.value = myCreationList.value[itemIndex].UGC_TYPE;
  currentImageIndex.value = 0;

  // 显示详情弹窗
  showContentDetail(myCreationList.value[itemIndex]);
};

// 点赞功能
const toggleWorkLike = async (id) => {
  if (!isLogin.value) {
    emit("showLoginTip");
    return;
  }

  const itemIndex = myCreationList.value.findIndex((item) => item.ID === id);
  if (itemIndex === -1) {
    console.error("作品不存在");
    return;
  }

  try {
    const { data, result, code } = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "like"),
      {
        params: JSON.stringify({
          ugcId: myCreationList.value[itemIndex]?.ID,
          likeType: +myCreationList.value[itemIndex]?.IS_LIKE ? 2 : 1,
        }),
      }
    );

    if (+result === 1) {
      // 切换点赞状态
      myCreationList.value[itemIndex].IS_LIKE = +myCreationList.value[itemIndex]
        .IS_LIKE
        ? 0
        : 1;

      // 更新点赞数
      if (myCreationList.value[itemIndex].IS_LIKE) {
        myCreationList.value[itemIndex].LIKE_COUNT++;
      } else {
        myCreationList.value[itemIndex].LIKE_COUNT = Math.max(
          0,
          myCreationList.value[itemIndex].LIKE_COUNT - 1
        );
      }

      // 更新显示的点赞数
      myCreationList.value[itemIndex].DISPLAY_LIKE_COUNT =
        +myCreationList.value[itemIndex]?.LIKE_COUNT >= 10000
          ? `${(+myCreationList.value[itemIndex]?.LIKE_COUNT / 10000).toFixed(
              1
            )}W`
          : +myCreationList.value[itemIndex]?.LIKE_COUNT >= 1000
          ? `${(+myCreationList.value[itemIndex]?.LIKE_COUNT / 1000).toFixed(
              1
            )}K`
          : myCreationList.value[itemIndex]?.LIKE_COUNT;
      
      // 数据埋点
      // 判断是图文还是纯文字：有图片列表且不为空则是图文，否则是文字
      const hasImages = myCreationList.value[itemIndex].DISPLAY_PIC_LIST && 
                        myCreationList.value[itemIndex].DISPLAY_PIC_LIST.length > 0 &&
                        myCreationList.value[itemIndex].DISPLAY_PIC_LIST.some(img => img && img.trim());
      const contentType = hasImages ? '图文' : '文字';
      
      window.activityDataLayer.push({
        eventName: "content_liking",
        eventType: contentType,
        eventValue: JSON.stringify({
          content_likes: myCreationList.value[itemIndex].LIKE_COUNT,
          player_account: myCreationList.value[itemIndex].LOGIN_ACCOUNT,
          player_uid: myCreationList.value[itemIndex].UID,
          content_creation_time: myCreationList.value[itemIndex].CREATE_TIME,
        }),
        aid: window.activity_name,
        gameId: window.game_id,
      });
    } else {
      console.error(`点赞失败: [${code}]`);
    }
  } catch (error) {
    console.error("切换点赞状态异常:", error);
  }
};

// 删除确认相关
const deleteConfirmState = ref(false);
const currentDeletingWork = ref(null);

// 审核通知相关
const auditNoticesPopupState = ref(false);
const auditNotices = ref([]);

const showDeleteConfirm = (work) => {
  window.activityDataLayer.push({
    eventName: "show_popup",
    eventType: "删除弹窗",
    aid: window.activity_name,
    gameId: window.game_id,
  });
  currentDeletingWork.value = work;
  deleteConfirmState.value = true;
};

const confirmDelete = async () => {
  if (!currentDeletingWork.value) return;

  try {
    const { data, result, code } = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "delete"),
      {
        params: JSON.stringify({
          ugcId: currentDeletingWork.value.ID,
        }),
      }
    );

    if (+result === 1) {
      // 从列表中删除作品
      const index = myCreationList.value.findIndex(
        (item) => item.ID === currentDeletingWork.value.ID
      );
      if (index !== -1) {
        myCreationList.value.splice(index, 1);
      }

      // 更新轮播图
      if (myCreationList.value.length > 0) {
        myWorkId.value = myCreationList.value[0].ID || "";
        myWorksImages.value = myCreationList.value[0].DISPLAY_PIC_LIST || [];
        currentImageIndex.value = 0;
      } else {
        myWorkId.value = "";
        myWorksImages.value = [];
        currentImageIndex.value = 0;
      }

      deleteConfirmState.value = false;
      currentDeletingWork.value = null;
    } else {
      console.error(`删除失败: [${code}]`);
    }
  } catch (error) {
    console.error("删除作品异常:", error);
  }
};

const cancelDelete = () => {
  deleteConfirmState.value = false;
  currentDeletingWork.value = null;
};

const showAuditNotices = async () => {
  try {
    window.activityDataLayer.push({
      eventName: "click_button",
      eventType: "审核通知",
      eventReason: "UGC创作",
      aid: window.activity_name,
      gameId: window.game_id,
    });
    window.activityDataLayer.push({
      eventName: "show_popup",
      eventType: "审核结果通知弹窗",
      aid: window.activity_name,
      gameId: window.game_id,
    });
    const { data, result, code } = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "message")
    );

    if (+result === 1) {
      let noticeData = data?.notifyList || [];
      auditNotices.value = noticeData.map((item) => {
        return {
          submitTime: item?.CREATE_TIME
            ? formatDate(new Date(item.CREATE_TIME))
            : "",
          title: item?.TITLE || "",
          reason: item?.REVIEW_TEXT || "",
          noticeTime: item?.REVIEW_TIME
            ? formatDate(new Date(item.REVIEW_TIME))
            : "",
        };
      });
      auditNoticesPopupState.value = true;
      // 更新通知状态（清除红点）
      await updateNoticeState();
    } else {
      console.error(`获取审核通知失败: [${code}]`);
    }
  } catch (error) {
    console.error("获取审核通知异常:", error);
  }
};

// 更新审核通知状态（清除红点）
const updateNoticeState = async () => {
  try {
    const { data, result, code } = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "clearRedPoint")
    );

    if (+result === 1) {
      isNotice.value = false;
    } else {
      console.error(`清除通知红点失败: [${code}]`);
    }
  } catch (error) {
    console.error("更新审核通知状态异常:", error);
  }
};

const backUgcList = () => {
  emit("switchNavTab", "ugc");
};

const showRulePopup = (content) => {
  // 可以传入自定义文案，也可以不传使用默认文案
  window.activityDataLayer.push({
    eventName: "click_button",
    eventType: "规则说明",
    eventReason: "创作中心",
    aid: window.activity_name,
    gameId: window.game_id,
  });
  showRulePopupFn(content);
};

const jumpToWebsite = () => {
  emit("jumpToWebsite");
};

const jumpToFacebook = () => {
  emit("jumpToFacebook");
};

const jumpToDiscord = () => {
  emit("jumpToDiscord");
};

// 分页切换
const handlePageChange = async (page) => {
  // 如果是省略号或无效页码，不处理
  if (typeof page !== "number" || page < 1 || page > pageInfo.totalPage) {
    return;
  }

  // 如果是当前页，不处理
  if (page === pageInfo.currentPage) {
    return;
  }

  pageInfo.currentPage = page;
  await loadMyCreations();

  // 滚动到列表顶部
  const worksList = document.querySelector(".works-list");
  if (worksList) {
    worksList.scrollTop = 0;
  }
};

// 监听登录状态变化
watch(
  isLogin,
  (newValue) => {
    if (newValue) {
      loadMyCreations();
    }
  },
  { immediate: false }
);

// 生命周期
onMounted(() => {
  viewPageStartTime.value = new Date();
  // 注册刷新函数供父组件调用
  if (refreshCenterData) {
    refreshCenterData.value = loadMyCreations;
  }
  loadMyCreations();
});

onUnmounted(() => {
  reportStayTime();
  // 清理刷新函数引用
  if (refreshCenterData) {
    refreshCenterData.value = null;
  }
});
</script>
